import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { add, divide, format, parse, subtract, zero } from '@fint/money';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Period } from '@/lib/dates';
import { useIsPhone } from '@/hooks/useIsPhone';
import {
  Card, CardContent, CardFacts, CardHeader, CardLine, CardRow, CardTitle, Cards, ColourDot, EmptyState, ErrorState, Money,
  SkeletonTable, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';
import { Signed, type Basis } from '@/features/dashboard/shared';
import {
  buildMonthsTable,
  isChartsBody,
  monthName,
  pivotByBook,
  readView,
  rememberView,
  type BookMonthRow,
  type BookTarget,
  type MonthRow,
  type MonthsTable,
  type MonthsView,
  type Pivot,
  type PivotCell,
} from './overview-months';

/** The `@fint/money` functions the table's maths runs on - see `overview-months.ts` for why they are handed in. */
const MONEY = { parse, add, subtract, divide, format, zero };

/**
 * "Month by month, all books": the per-book dashboard's table, across books.
 *
 * ── WHAT IT IS THE SIBLING OF ──
 * `MoneyInAndOut`'s "Month by month" card: the same columns - money in,
 * money out, profit, the running total, the goal - with the same Averages
 * row over the months with activity, and the same rule that a month with
 * nothing in it is a row of zeros rather than a missing row. The owner's
 * spreadsheet had that table per book; this is the one that adds the sheets
 * up, net of what the books paid each other, with each book's own months
 * one tap away under the month.
 *
 * ── THE SAME QUERY AS THE CHARTS ──
 * The charts above already load `/practice/overview/charts` for this period,
 * basis AND organisation, and the query key here is theirs - every argument
 * of it - so React Query serves both from one request. Miss one argument and
 * the table quietly becomes a second, differently-scoped request: that is how
 * a demo shelf's millions once landed in a table headed with the real books'
 * name. The goals are the one thing that response does not carry: a target is
 * set inside a book, so each counted book's targets are fetched under that
 * book's own org id, through the api client's per-call `orgId`. A book whose
 * goals fail to load keeps its months and loses its goal, with a line saying
 * so - the table stands.
 *
 * ── THE GOAL COLUMNS GO WHEN THERE IS NO GOAL ──
 * As on the dashboard (UX §7.8): "there is no goal" and "you missed the
 * goal by nothing" are different facts, and a column of dashes is the
 * reader's job to interpret. When some month has a goal, the months without
 * one show a dash, never a zero.
 *
 * ── WHAT IT WILL NOT DO WITH MONEY ──
 * Every figure comes out of `buildMonthsTable`, which hands the arithmetic
 * to `@fint/money`. Nothing here passes an amount through `Number()`.
 */
export function OverviewMonths({ period, basis, organisationId }: { period: Period; basis: Basis; organisationId?: string }) {
  const isPhone = useIsPhone();
  const charts = useQuery({
    // The charts' own key, argument for argument: one request for the charts
    // and the table.
    queryKey: ['practice-overview-charts', organisationId ?? null, period.from, period.to, basis],
    queryFn: () => api.get<unknown>(`/practice/overview/charts${qs({ from: period.from, to: period.to, basis, organisationId })}`),
  });
  const data = isChartsBody(charts.data) ? charts.data : undefined;
  const counted = data ? data.books.filter((book) => !book.withheld && book.inTotals) : [];

  // One small query per counted book, AS that book. `period.from` is the
  // first of a month and `period.to` the last of one - the picker only
  // ever asks for whole months - so the range lands exactly on the
  // `YYYY-MM-01` rows a target is stored under.
  const targets = useQueries({
    queries: counted.map((book) => ({
      queryKey: ['overview-targets', book.orgId, period.from, period.to],
      queryFn: () =>
        api.get<{ items: Array<Omit<BookTarget, 'orgId'>> }>(
          `/targets${qs({ periodType: 'month', metric: 'income', from: period.from, to: period.to })}`,
          { orgId: book.orgId },
        ),
    })),
  });

  const [view, setView] = useState<MonthsView>(() => readView(safeStorage()));
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (month: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });
  const choose = (next: MonthsView) => {
    setView(next);
    rememberView(safeStorage(), next);
  };

  const header = (
    <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
      <CardTitle>
        <CalendarDays className="size-4 text-muted-foreground" aria-hidden />
        Month by month, all books
      </CardTitle>
      <div role="group" aria-label="Show the months" className="flex gap-1.5" data-testid="overview-months-view">
        {(
          [
            ['net', 'All books'],
            ['books', 'Per book'],
          ] as const
        ).map(([key, text]) => (
          <button key={key} type="button" aria-pressed={view === key} onClick={() => choose(key)} className={chip(view === key)}>
            {text}
          </button>
        ))}
      </div>
    </CardHeader>
  );

  if (charts.isError) {
    return (
      <Card data-testid="overview-months">
        {header}
        <ErrorState error={charts.error} onRetry={() => { void charts.refetch(); }} />
      </Card>
    );
  }
  if (charts.isPending || targets.some((query) => query.isPending)) {
    return (
      <Card data-testid="overview-months">
        {header}
        <div data-testid="overview-months-loading">
          <SkeletonTable rows={4} cols={6} />
        </div>
      </Card>
    );
  }
  if (!data) {
    // A body of another shape - an older server, a mocked route - is a
    // stated failure, not a blank table or a thrown render.
    return (
      <Card data-testid="overview-months">
        {header}
        <ErrorState error={new Error('The months came back in a shape this page does not understand. Reload to try again.')} />
      </Card>
    );
  }

  const loaded: BookTarget[] = targets.flatMap((query, index) => {
    // Tag each row with the book it came from: the server does not repeat
    // the org id on a target, and the query was made as that book.
    const orgId = counted[index]?.orgId;
    return query.data && orgId ? query.data.items.map((item) => ({ ...item, orgId })) : [];
  });
  const failed = targets.flatMap((query, index) => (query.isError ? [{ name: counted[index]?.name ?? 'a book', retry: query.refetch }] : []));
  const table = buildMonthsTable(data, loaded, MONEY);
  const currency = table.currency;

  return (
    <Card data-testid="overview-months">
      {header}
      {table.countedBooks === 0 ? (
        <EmptyState
          title="No book to add up"
          description="Every book here is either not yours to report on or keeps its books in another currency — see the notes under the table."
        />
      ) : !table.anyMovement ? (
        <EmptyState
          title={`Nothing moved in ${period.label}`}
          description="An invoice, an expense or a deposit dated in this period will show here, month by month."
        />
      ) : (
        <CardContent>
          {view === 'net' ? (
            isPhone ? (
              <NetCards table={table} open={open} toggle={toggle} />
            ) : (
              <NetTable table={table} open={open} toggle={toggle} />
            )
          ) : isPhone ? (
            <PivotCards pivot={pivotByBook(table, MONEY)} currency={currency} />
          ) : (
            <PivotTable pivot={pivotByBook(table, MONEY)} currency={currency} />
          )}
          {table.totals.between.minor !== 0 ? (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="overview-months-between">
              <Money money={table.totals.between} currency={currency} /> moved between your books is not counted above
              {view === 'books' ? ' in the Net columns; each book’s own columns still include it' : ''}.
            </p>
          ) : null}
          {failed.length > 0 ? (
            <p className="mt-2 text-xs text-muted-foreground" role="status" data-testid="overview-months-goals-failed">
              The goal for {failed.map((book) => book.name).join(' and ')} could not be loaded, so it is not in the Goal column.{' '}
              <button type="button" className="underline" data-tap-exempt="inline text link in a sentence" onClick={() => { for (const book of failed) void book.retry(); }}>
                Try again
              </button>
            </p>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}

// ── the net table ─────────────────────────────────────────────────────────

function NetTable({ table, open, toggle }: { table: MonthsTable; open: ReadonlySet<string>; toggle: (month: string) => void }) {
  const currency = table.currency;
  const goals = table.hasAnyGoal;
  const cols = goals ? 7 : 5;
  return (
    <Table data-testid="overview-months-table">
      <THead>
        <TR>
          <TH>Month</TH>
          <TH numeric>Money in</TH>
          <TH numeric>Money out</TH>
          <TH numeric>Profit</TH>
          {/* "Running profit", not "balance": it is the profits summed from
              the period's first month, and "balance" in an accounting
              product means cash - which this is not. */}
          <TH numeric>Running profit</TH>
          {goals ? <TH numeric>Goal</TH> : null}
          {goals ? <TH numeric>vs goal</TH> : null}
        </TR>
      </THead>
      <TBody>
        {table.rows.map((row) => {
          const expanded = open.has(row.month);
          return (
            <MonthRows key={row.month} row={row} currency={currency} goals={goals} expanded={expanded} onToggle={() => toggle(row.month)} cols={cols} />
          );
        })}
        <TR className="border-t-2 border-t-border bg-muted/40 font-medium" data-testid="overview-months-total">
          <TD>Total</TD>
          <TD numeric><Money money={table.totals.income} currency={currency} showSymbol={false} /></TD>
          <TD numeric><Money money={table.totals.expense} currency={currency} showSymbol={false} /></TD>
          <TD numeric><Signed value={format(table.totals.profit)} currency={currency} /></TD>
          <TD numeric><Signed value={format(table.totals.profit)} currency={currency} /></TD>
          {goals ? (
            <TD numeric>
              {table.totals.goal ? <Money money={table.totals.goal} currency={currency} showSymbol={false} /> : <Dash label="no goal" />}
              <GoalMonths table={table} />
            </TD>
          ) : null}
          {goals ? <TD numeric>{table.totals.vsGoal ? <Signed value={format(table.totals.vsGoal)} currency={currency} /> : <Dash label="no goal" />}</TD> : null}
        </TR>
        <TR className="font-medium" data-testid="overview-months-average">
          {/* The divisor is printed, because averaging a half-year over
              twelve months halves every figure and the reader would have
              no way to know. */}
          <TD>{averageLabel(table)}</TD>
          <TD numeric><Money money={table.averages.income} currency={currency} showSymbol={false} /></TD>
          <TD numeric><Money money={table.averages.expense} currency={currency} showSymbol={false} /></TD>
          <TD numeric><Signed value={format(table.averages.profit)} currency={currency} /></TD>
          <TD />
          {goals ? <TD /> : null}
          {goals ? <TD /> : null}
        </TR>
      </TBody>
    </Table>
  );
}

/** One month's row and, when it is open, one row per book under it. */
function MonthRows({
  row,
  currency,
  goals,
  expanded,
  onToggle,
  cols,
}: {
  row: MonthRow;
  currency: string;
  goals: boolean;
  expanded: boolean;
  onToggle: () => void;
  cols: number;
}) {
  return (
    <>
      <TR data-testid={`overview-months-row-${row.month}`} className={expanded ? 'bg-muted/20' : undefined}>
        <TD>
          <MonthButton month={row.month} expanded={expanded} onToggle={onToggle} />
        </TD>
        <TD numeric><Money money={row.income} currency={currency} showSymbol={false} /></TD>
        <TD numeric><Money money={row.expense} currency={currency} showSymbol={false} /></TD>
        <TD numeric><Signed value={format(row.profit)} currency={currency} /></TD>
        <TD numeric><Signed value={format(row.running)} currency={currency} /></TD>
        {goals ? <TD numeric>{row.goal ? <Money money={row.goal} currency={currency} showSymbol={false} /> : <Dash label="no goal" />}</TD> : null}
        {goals ? <TD numeric>{row.vsGoal ? <Signed value={format(row.vsGoal)} currency={currency} /> : <Dash label="no goal" />}</TD> : null}
      </TR>
      {expanded
        ? row.books.map((book) => (
            <TR key={book.orgId} className="bg-muted/20 text-xs" data-testid={`overview-months-book-${row.month}`} data-org-id={book.orgId}>
              <TD wrap className="pl-8">
                <BookName book={book} />
              </TD>
              {book.income && book.expense && book.profit ? (
                <>
                  <TD numeric><Money money={book.income} currency={book.currency} showSymbol={false} /></TD>
                  <TD numeric><Money money={book.expense} currency={book.currency} showSymbol={false} /></TD>
                  <TD numeric><Signed value={format(book.profit)} currency={book.currency} /></TD>
                </>
              ) : (
                Array.from({ length: 3 }).map((_, i) => (
                  <TD key={i} numeric><Dash label="not shown" /></TD>
                ))
              )}
              <TD />
              {goals ? <TD numeric>{book.target ? <Money money={book.target} currency={book.currency} showSymbol={false} /> : <Dash label="no goal" />}</TD> : null}
              {goals ? <TD numeric>{book.vsTarget ? <Signed value={format(book.vsTarget)} currency={book.currency} /> : <Dash label="no goal" />}</TD> : null}
            </TR>
          ))
        : null}
      {expanded && row.books.length === 0 ? (
        <TR className="bg-muted/20 text-xs text-muted-foreground">
          <TD colSpan={cols} wrap>No book has a row for this month.</TD>
        </TR>
      ) : null}
    </>
  );
}

// ── the phone: one card per month ─────────────────────────────────────────

function NetCards({ table, open, toggle }: { table: MonthsTable; open: ReadonlySet<string>; toggle: (month: string) => void }) {
  const currency = table.currency;
  return (
    <Cards data-testid="overview-months-cards">
      {table.rows.map((row) => {
        const expanded = open.has(row.month);
        return (
          <CardRow key={row.month} data-testid={`overview-months-row-${row.month}`}>
            <CardLine
              title={<MonthButton month={row.month} expanded={expanded} onToggle={() => toggle(row.month)} />}
              value={<Signed value={format(row.profit)} currency={currency} />}
              sub={
                <>
                  running <Signed value={format(row.running)} currency={currency} />
                </>
              }
            />
            <CardFacts>
              <span>
                in <Money money={row.income} currency={currency} showSymbol={false} />
              </span>
              <span>
                out <Money money={row.expense} currency={currency} showSymbol={false} />
              </span>
              {table.hasAnyGoal ? (
                <span>
                  goal{' '}
                  {row.goal && row.vsGoal ? (
                    <>
                      <Money money={row.goal} currency={currency} showSymbol={false} /> · <Signed value={format(row.vsGoal)} currency={currency} />
                    </>
                  ) : (
                    <Dash label="no goal" />
                  )}
                </span>
              ) : null}
            </CardFacts>
            {expanded ? (
              <ul className="mt-2 space-y-2 border-l-2 border-border pl-3 text-xs">
                {row.books.map((book) => (
                  <li key={book.orgId} data-testid={`overview-months-book-${row.month}`} data-org-id={book.orgId}>
                    <BookName book={book} />
                    {book.income && book.expense && book.profit ? (
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                        <span>
                          in <Money money={book.income} currency={book.currency} showSymbol={false} />
                        </span>
                        <span>
                          out <Money money={book.expense} currency={book.currency} showSymbol={false} />
                        </span>
                        <span>
                          <Signed value={format(book.profit)} currency={book.currency} />
                        </span>
                        {book.target && book.vsTarget ? (
                          <span>
                            goal <Money money={book.target} currency={book.currency} showSymbol={false} /> · <Signed value={format(book.vsTarget)} currency={book.currency} />
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-muted-foreground">
                        <Dash label="not shown" />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </CardRow>
        );
      })}
      <CardRow className="bg-muted/40 font-medium" data-testid="overview-months-total">
        <CardLine
          title="Total"
          value={<Signed value={format(table.totals.profit)} currency={currency} />}
          sub={
            table.hasAnyGoal ? (
              <>
                goal{' '}
                {table.totals.goal && table.totals.vsGoal ? (
                  <>
                    <Money money={table.totals.goal} currency={currency} showSymbol={false} /> · <Signed value={format(table.totals.vsGoal)} currency={currency} />
                  </>
                ) : (
                  <Dash label="no goal" />
                )}
              </>
            ) : undefined
          }
        />
        <CardFacts>
          <span>
            in <Money money={table.totals.income} currency={currency} showSymbol={false} />
          </span>
          <span>
            out <Money money={table.totals.expense} currency={currency} showSymbol={false} />
          </span>
          <GoalMonths table={table} />
        </CardFacts>
      </CardRow>
      <CardRow className="font-medium" data-testid="overview-months-average">
        <CardLine title={averageLabel(table)} value={<Signed value={format(table.averages.profit)} currency={currency} />} />
        <CardFacts>
          <span>
            in <Money money={table.averages.income} currency={currency} showSymbol={false} />
          </span>
          <span>
            out <Money money={table.averages.expense} currency={currency} showSymbol={false} />
          </span>
        </CardFacts>
      </CardRow>
    </Cards>
  );
}

// ── the spreadsheet view: a column group per book ─────────────────────────

function PivotTable({ pivot, currency }: { pivot: Pivot; currency: string }) {
  return (
    <Table data-testid="overview-months-pivot">
      <THead>
        <TR>
          <TH rowSpan={2} className="align-bottom">Month</TH>
          {pivot.books.map((book) => (
            <TH key={book.orgId} colSpan={3} scope="colgroup" className="border-l border-border text-center normal-case tracking-normal">
              <span className="inline-flex items-center gap-1.5">
                <ColourDot colour={book.colour} className="h-2 w-2" />
                {book.name}
                {book.inTotals || book.withheld ? null : <span className="font-normal"> · {book.currency}</span>}
              </span>
            </TH>
          ))}
          <TH colSpan={3} scope="colgroup" className="border-l border-border text-center normal-case tracking-normal">Net, all books</TH>
        </TR>
        <TR>
          {[...pivot.books, { orgId: 'net' }].map((book) => (
            <PivotSubheads key={book.orgId} />
          ))}
        </TR>
      </THead>
      <TBody>
        {pivot.rows.map((row) => (
          <TR key={row.month} data-testid={`overview-months-row-${row.month}`}>
            <TD className="font-medium">{monthName(row.month)}</TD>
            {row.cells.map((cell, index) => (
              <PivotCells key={pivot.books[index]?.orgId ?? index} cell={cell} currency={pivot.books[index]?.currency ?? currency} />
            ))}
            <PivotCells cell={row.net} currency={currency} />
          </TR>
        ))}
        <TR className="border-t-2 border-t-border bg-muted/40 font-medium" data-testid="overview-months-total">
          <TD>Total</TD>
          {pivot.totals.cells.map((cell, index) => (
            <PivotCells key={pivot.books[index]?.orgId ?? index} cell={cell} currency={pivot.books[index]?.currency ?? currency} />
          ))}
          <PivotCells cell={pivot.totals.net} currency={currency} />
        </TR>
      </TBody>
    </Table>
  );
}

function PivotSubheads() {
  return (
    <>
      <TH numeric className="border-l border-border">In</TH>
      <TH numeric>Out</TH>
      <TH numeric>Profit</TH>
    </>
  );
}

function PivotCells({ cell, currency }: { cell: PivotCell | null; currency: string }) {
  if (!cell) {
    return (
      <>
        <TD numeric className="border-l border-border"><Dash label="not shown" /></TD>
        <TD numeric><Dash label="not shown" /></TD>
        <TD numeric><Dash label="not shown" /></TD>
      </>
    );
  }
  return (
    <>
      <TD numeric className="border-l border-border"><Money money={cell.income} currency={currency} showSymbol={false} /></TD>
      <TD numeric><Money money={cell.expense} currency={currency} showSymbol={false} /></TD>
      <TD numeric><Signed value={format(cell.profit)} currency={currency} /></TD>
    </>
  );
}

function PivotCards({ pivot, currency }: { pivot: Pivot; currency: string }) {
  const line = (cell: PivotCell | null, cellCurrency: string) =>
    cell ? (
      <span className="flex flex-wrap gap-x-3 text-muted-foreground">
        <span>
          in <Money money={cell.income} currency={cellCurrency} showSymbol={false} />
        </span>
        <span>
          out <Money money={cell.expense} currency={cellCurrency} showSymbol={false} />
        </span>
        <Signed value={format(cell.profit)} currency={cellCurrency} />
      </span>
    ) : (
      <span className="text-muted-foreground">
        <Dash label="not shown" />
      </span>
    );
  const block = (row: { cells: Array<PivotCell | null>; net: PivotCell }) => (
    <ul className="mt-2 space-y-1.5 text-xs">
      {pivot.books.map((book, index) => (
        <li key={book.orgId} className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <ColourDot colour={book.colour} className="h-2 w-2" />
            {book.name}
            {book.inTotals || book.withheld ? null : <span className="font-normal text-muted-foreground"> · {book.currency}</span>}
          </span>
          {line(row.cells[index] ?? null, book.currency)}
        </li>
      ))}
      <li className="flex flex-col gap-0.5 border-t border-border pt-1.5">
        <span className="font-medium">Net, all books</span>
        {line(row.net, currency)}
      </li>
    </ul>
  );
  return (
    <Cards data-testid="overview-months-pivot">
      {pivot.rows.map((row) => (
        <CardRow key={row.month} data-testid={`overview-months-row-${row.month}`}>
          <CardLine title={monthName(row.month)} value={<Signed value={format(row.net.profit)} currency={currency} />} sub="net profit" />
          {block(row)}
        </CardRow>
      ))}
      <CardRow className="bg-muted/40" data-testid="overview-months-total">
        <CardLine title={<span className="font-semibold">Total</span>} value={<Signed value={format(pivot.totals.net.profit)} currency={currency} />} sub="net profit" />
        {block(pivot.totals)}
      </CardRow>
    </Cards>
  );
}

// ── the pieces ────────────────────────────────────────────────────────────

/**
 * The month, as the thing to tap. `aria-expanded` is on the button, which
 * is what a screen reader reads out, and the chevron turns to say the same
 * thing to a sighted person. Tall enough for a thumb on a touch screen.
 */
function MonthButton({ month, expanded, onToggle }: { month: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded font-medium hover:text-primary touch:min-h-11"
    >
      <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')} aria-hidden />
      {monthName(month)}
    </button>
  );
}

/**
 * A book's name in a month's expansion, with the dot the switcher shows it
 * by, and under it the two things worth a line: what moved to or from
 * another of the person's books this month, and the note for a book whose
 * figures are not here.
 */
function BookName({ book }: { book: BookMonthRow }) {
  const betweenIn = book.betweenIn && book.betweenIn.minor !== 0 ? book.betweenIn : null;
  const betweenOut = book.betweenOut && book.betweenOut.minor !== 0 ? book.betweenOut : null;
  return (
    <>
      <span className="inline-flex items-center gap-2 font-medium">
        <ColourDot colour={book.colour} className="h-2 w-2" />
        {book.name}
      </span>
      {betweenIn ? (
        <span className="block text-muted-foreground">
          <Money money={betweenIn} currency={book.currency} showSymbol={false} /> in from another of your books
        </span>
      ) : null}
      {betweenOut ? (
        <span className="block text-muted-foreground">
          <Money money={betweenOut} currency={book.currency} showSymbol={false} /> out to another of your books
        </span>
      ) : null}
      {book.note ? <span className="block text-muted-foreground">{book.note}</span> : null}
    </>
  );
}

/** How many months the Total's goal covers, when that is fewer than the period has. */
function GoalMonths({ table }: { table: MonthsTable }) {
  if (!table.totals.goal || table.totals.goalMonths >= table.rows.length) return null;
  return (
    <span className="block text-xs font-normal text-muted-foreground">
      over {table.totals.goalMonths} of {table.rows.length} months
    </span>
  );
}

function averageLabel(table: MonthsTable): string {
  return `Average (${table.averages.months} ${table.averages.months === 1 ? 'month' : 'months'})`;
}

/** An em dash with an accessible name, so a screen reader does not read a silence. */
function Dash({ label }: { label: string }) {
  return (
    <span aria-label={label} className="text-muted-foreground">
      —
    </span>
  );
}

/** The chip style the basis toggle and the charts' switches use, so the page's controls read as one family. Tall enough for a thumb. */
function chip(active: boolean): string {
  return cn(
    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium touch:min-h-11 touch:min-w-11',
    active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted active:bg-muted',
  );
}

/** `localStorage`, or nothing: a locked-down browser throws on the accessor itself. */
function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
